import * as React from 'react';
import useHeartbeat from '../../hooks/useHeartbeat';

// Maps the heartbeat's connected/reconnecting/offline states onto the SAME
// badge/dot tone + label convention as ItemDetails' save-status indicator
// (see views/ItemDetails.js `saveIndicator` / styles/ds.css `.badge`/`.dot`),
// so this reads as part of the same design system rather than a bespoke widget.
const STATE = {
  connected: { tone: 'success', label: 'Connected' },
  reconnecting: { tone: 'warning', label: 'Reconnecting…' },
  offline: { tone: 'error', label: 'Offline' },
};

// Dot-only on mobile — mirrors NotificationBell's scoped-<style> responsive
// swap (this file's own doc comment) rather than editing the shared ds.css.
const RESPONSIVE_STYLE = `
  @media (max-width: 640px) {
    .conn-status-label { display: none; }
  }
`;

/**
 * Backend-connected status dot (#161). Distinct from the browser's
 * `navigator.onLine` (see hooks/useHeartbeat.js) — this reflects whether the
 * BACKEND is actually reachable, not just whether the device has a network
 * interface up. Dot-only on mobile (the label crowds the top bar, same
 * trade-off ItemDetails' save-status badge already makes); full label on
 * desktop via the `conn-status-label` class (hidden by screens.css <640px,
 * mirroring `.badge-label`).
 */
export default function ConnectionStatusDot({ useHeartbeatHook = useHeartbeat } = {}) {
  const { status } = useHeartbeatHook();
  const { tone, label } = STATE[status] || STATE.connected;

  return (
    <span
      className={`badge ${tone}`}
      data-testid="conn-status"
      data-status={status}
      title={label}
      style={{ flexShrink: 0 }}
    >
      <style>{RESPONSIVE_STYLE}</style>
      <span className={`dot ${tone}`} />
      <span className="badge-label conn-status-label">{label}</span>
      {/* Announce the change for screen-reader users, same polite-live-region
          pattern as ItemDetails' save-status indicator (the dot's colour alone
          conveys nothing to them). */}
      <span
        role="status"
        aria-live="polite"
        data-testid="conn-status-live"
        style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
      >
        {label}
      </span>
    </span>
  );
}
